/**
 * Invoice split mode for a work order (maintenance).
 *
 * Single source of truth for "what happens when the user clicks Luo lasku", shared by
 * the create-invoice button labels and CreateInvoiceDialog. The backend
 * (invoicing/services/invoice_service.py create_split_invoices) applies the same rules.
 *
 * Modes:
 *  - null              → one normal invoice (to the billing customer if set, else the car owner)
 *  - 'vat_company'     → insurance ALV-yritys net split (insurance switch + ALV toggle on)
 *  - 'insurance'       → insurance gross split: insurer invoice + omavastuu invoice
 *  - 'billing_customer'→ non-insurance split: billing customer invoice + customer invoice
 *                        (customer-targeted lines and/or an optional omavastuu)
 */

export const hasCustomerTargetedLines = (maintenance) =>
  (maintenance?.parts || []).some((p) => p.invoice_target === 'customer')
  || (maintenance?.jobs || []).some((j) => j.invoice_target === 'customer');

export const getInvoiceSplitMode = (maintenance) => {
  if (!maintenance?.billing_customer) return null;

  const isInsurance = !!maintenance.billing_customer_is_insurance;
  // ALV-yritys is an insurance-only concept; a stale flag with the switch off is ignored.
  if (isInsurance && maintenance.insurance_vat_deductible) return 'vat_company';

  const deductible = parseFloat(maintenance.deductible_amount) || 0;
  const customerLines = hasCustomerTargetedLines(maintenance);

  if (isInsurance) {
    // Insurance mode requires an explicit omavastuu (0 is fine) before splitting.
    const deductibleIsSet = maintenance.deductible_amount != null;
    return deductibleIsSet && (deductible > 0 || customerLines) ? 'insurance' : null;
  }

  return deductible > 0 || customerLines ? 'billing_customer' : null;
};

/** Label key for the create-invoice button / dialog title for a given mode. */
export const getCreateInvoiceLabelKey = (mode) => {
  if (mode === 'vat_company' || mode === 'insurance') return 'Create insurance invoices';
  if (mode === 'billing_customer') return 'Create split invoices';
  return 'Create invoice';
};

/**
 * Notes a new invoice starts with: the work order description (when shown to the customer)
 * plus the customer notes flagged for the invoice. Shared by CreateInvoiceDialog and the
 * quick payment buttons so both create the same invoice.
 */
export const buildDefaultInvoiceNotes = (maintenance) => {
  if (!maintenance) return '';
  const parts = [];
  if (maintenance.show_description_to_customer && maintenance.description) {
    parts.push(maintenance.description);
  }
  if (maintenance.notes) {
    const invoiceNotes = maintenance.notes
      .filter((n) => n.note_type === 'customer' && n.show_on_invoice)
      .map((n) => n.text)
      .filter(Boolean);
    parts.push(...invoiceNotes);
  }
  return parts.join('\n');
};
